import { useCallback, useEffect, useRef, useState } from "react";
import { adminStorage, adminService } from "../../features/admin/service";
import { apiClient } from "../../lib/http/apiClient";
import { parseApiError } from "../../lib/http/apiErrors";

// Admin-driven partner onboarding: renders the type-specific questionnaire
// returned by GET /api/admin/partners/{owner}/onboarding and saves answers via
// PATCH /api/admin/partners/{owner}/profile (same partial-update path the
// partner's own PATCH /me uses). Field `type` drives which control we render.

interface Field {
  key: string;
  label: string;
  type: string;
  required: boolean;
  options?: string[];
  value?: any;
}
interface Step {
  key: string;
  label: string;
  step: number;
  required: boolean;
  fields: Field[];
}
interface Questionnaire {
  partner_type: string | null;
  steps: Step[];
  status?: { completed: boolean; missing_steps: string[] };
}

const READONLY_KEYS = new Set(["partner_type"]);
const READONLY_REASON: Record<string, string> = {
  partner_type: "Locked — changing it swaps which type-specific questionnaire applies (IATA codes, hotel brands, etc.) and could orphan answers already saved under the old type. Delete and re-provision the partner if the type was wrong.",
};
const LIST_TYPES = new Set(["tags", "country_list", "city_list", "media_list"]);

function adminHeaders() {
  const token = adminStorage.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const labelCls = "block text-[10px] uppercase tracking-wide text-gray-500 mb-1";
const inputCls =
  "w-full px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500/40";

// Upload widget for `media` fields (logo, channel banner). Uploads the file via
// the admin storage endpoint and stores the returned render url as the answer.
function MediaUpload({
  label, value, required, onChange,
}: {
  label: string;
  value?: string;
  required?: boolean;
  onChange: (url: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    setErr("");
    try {
      const { url } = await adminService.uploadCommunityMedia(file);
      onChange(url);
    } catch (e) {
      setErr(parseApiError(e).message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <label className={labelCls}>{label}{required && <span className="text-red-400"> *</span>}</label>
      <div className="flex items-center gap-3">
        {value ? (
          <img src={value} alt={label} className="h-12 w-12 rounded-lg object-cover border border-gray-700" />
        ) : (
          <div className="h-12 w-12 rounded-lg border border-dashed border-gray-700 flex items-center justify-center text-[9px] text-gray-600">none</div>
        )}
        <div className="flex flex-col gap-1">
          <input ref={inputRef} type="file" accept="image/*" className="hidden"
            onChange={(e) => void pick(e.target.files?.[0])} />
          <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
            className="px-3 py-1 bg-gray-800 hover:bg-gray-700 rounded-lg text-[11px] text-gray-300 disabled:opacity-50">
            {uploading ? "Uploading…" : value ? "Replace" : "Upload"}
          </button>
          {value && (
            <button type="button" onClick={() => onChange("")}
              className="px-3 py-1 rounded-lg text-[11px] text-red-400 hover:bg-red-900/30">
              Remove
            </button>
          )}
        </div>
      </div>
      {err && <p className="text-[11px] text-red-400 mt-1">{err}</p>}
    </div>
  );
}

// Chip/tag input for list fields (countries, cities, tags, …). Each entry is a
// removable chip; commit with Enter or comma, delete the last with Backspace.
// Fixes the old comma-split-on-keystroke bug that merged distinct values.
function TagInput({
  label, required, value, onChange,
}: {
  label: string;
  required?: boolean;
  value: any;
  onChange: (next: string[]) => void;
}) {
  const arr: string[] = Array.isArray(value) ? value : value ? [String(value)] : [];
  const [draft, setDraft] = useState("");

  const commit = (raw: string) => {
    const parts = raw.split(",").map((s) => s.trim()).filter(Boolean);
    const merged = [...arr];
    parts.forEach((p) => { if (!merged.includes(p)) merged.push(p); });
    if (merged.length !== arr.length) onChange(merged);
    setDraft("");
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      if (draft.trim()) commit(draft);
    } else if (e.key === "Backspace" && !draft && arr.length) {
      onChange(arr.slice(0, -1));
    }
  };

  return (
    <div>
      <label className={labelCls}>{label}{required && <span className="text-red-400"> *</span>}</label>
      <div className="flex flex-wrap items-center gap-1.5 px-2 py-1.5 bg-gray-800 border border-gray-700 rounded-lg focus-within:ring-2 focus-within:ring-indigo-500/40">
        {arr.map((t, i) => (
          <span key={`${t}-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-indigo-500/15 border border-indigo-500/30 text-indigo-200 text-xs">
            {t}
            <button type="button" onClick={() => onChange(arr.filter((_, j) => j !== i))}
              className="text-indigo-300/70 hover:text-white leading-none">×</button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={() => draft.trim() && commit(draft)}
          placeholder={arr.length ? "" : "type and press Enter"}
          className="flex-1 min-w-[90px] bg-transparent text-white text-xs outline-none placeholder-gray-600 py-0.5"
        />
      </div>
    </div>
  );
}

function setDotted(target: any, path: string, value: any) {
  const parts = path.split(".");
  let cur = target;
  for (let i = 0; i < parts.length - 1; i++) {
    if (typeof cur[parts[i]] !== "object" || cur[parts[i]] === null) cur[parts[i]] = {};
    cur = cur[parts[i]];
  }
  cur[parts[parts.length - 1]] = value;
}

export default function PartnerOnboardingEditor({ ownerId }: { ownerId: string }) {
  const [data, setData] = useState<Questionnaire | null>(null);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get<Questionnaire>(
        `/api/admin/partners/${ownerId}/onboarding`,
        { headers: adminHeaders() },
      );
      setData(data);
      // seed answers from saved field values
      const seed: Record<string, any> = {};
      (data.steps || []).forEach((s) =>
        (s.fields || []).forEach((f) => { if (f.value != null) seed[f.key] = f.value; }),
      );
      setAnswers(seed);
    } catch (e) {
      setErr(parseApiError(e).message);
    } finally {
      setLoading(false);
    }
  }, [ownerId]);

  useEffect(() => { void load(); }, [load]);

  const set = (key: string, value: any) => setAnswers((a) => ({ ...a, [key]: value }));

  const save = async () => {
    setSaving(true);
    setErr("");
    setMsg("");
    try {
      const payload: any = {};
      Object.entries(answers).forEach(([k, v]) => {
        if (READONLY_KEYS.has(k)) return;            // org name/type fixed at provisioning
        if (v === "" || v == null) return;
        setDotted(payload, k, v);
      });
      await apiClient.patch(`/api/admin/partners/${ownerId}/profile`, payload, {
        headers: adminHeaders(),
      });
      setMsg("Onboarding saved.");
      await load();
      setTimeout(() => setMsg(""), 3000);
    } catch (e) {
      setErr(parseApiError(e).message);
    } finally {
      setSaving(false);
    }
  };

  const renderField = (f: Field) => {
    const v = answers[f.key];
    const ro = READONLY_KEYS.has(f.key);
    const req = f.required ? <span className="text-red-400"> *</span> : null;

    if (f.type === "media") {
      return (
        <MediaUpload label={f.label} value={typeof v === "string" ? v : ""}
          required={f.required} onChange={(url) => set(f.key, url)} />
      );
    }
    if (f.type === "bool") {
      return (
        <label className="flex items-center gap-2 text-xs text-gray-300">
          <input type="checkbox" checked={!!v} disabled={ro}
            onChange={(e) => set(f.key, e.target.checked)} />
          {f.label}{req}
        </label>
      );
    }
    if (f.type === "select") {
      return (
        <div>
          <label className={labelCls}>{f.label}{req}</label>
          <select className={inputCls} value={v ?? ""} disabled={ro}
            onChange={(e) => set(f.key, e.target.value)}>
            <option value="">—</option>
            {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
          {ro && READONLY_REASON[f.key] && <p className="text-[10px] text-gray-600 mt-1">{READONLY_REASON[f.key]}</p>}
        </div>
      );
    }
    if (f.type === "multiselect") {
      const arr: string[] = Array.isArray(v) ? v : [];
      return (
        <div>
          <label className={labelCls}>{f.label}{req}</label>
          <div className="flex flex-wrap gap-1.5">
            {(f.options || []).map((o) => {
              const on = arr.includes(o);
              return (
                <button key={o} type="button"
                  onClick={() => set(f.key, on ? arr.filter((x) => x !== o) : [...arr, o])}
                  className={`px-2.5 py-1 rounded-lg text-[11px] border transition-colors ${
                    on ? "bg-indigo-600 border-indigo-500 text-white"
                       : "bg-gray-800 border-gray-700 text-gray-400 hover:bg-gray-700"}`}>
                  {o}
                </button>
              );
            })}
          </div>
        </div>
      );
    }
    if (f.type === "textarea") {
      return (
        <div>
          <label className={labelCls}>{f.label}{req}</label>
          <textarea rows={2} className={`${inputCls} resize-none`} value={v ?? ""} disabled={ro}
            onChange={(e) => set(f.key, e.target.value)} />
        </div>
      );
    }
    if (f.type === "contact") {
      const obj = (v && typeof v === "object") ? v : {};
      const patch = (k: string, val: string) => set(f.key, { ...obj, [k]: val });
      return (
        <div>
          <label className={labelCls}>{f.label}{req}</label>
          <div className="grid grid-cols-3 gap-2">
            <input className={inputCls} placeholder="name" value={obj.name ?? ""} onChange={(e) => patch("name", e.target.value)} />
            <input className={inputCls} placeholder="email" value={obj.email ?? ""} onChange={(e) => patch("email", e.target.value)} />
            <input className={inputCls} placeholder="phone" value={obj.phone ?? ""} onChange={(e) => patch("phone", e.target.value)} />
          </div>
        </div>
      );
    }
    if (f.type === "rep_list") {
      return (
        <div>
          <label className={labelCls}>{f.label}</label>
          <p className="text-[11px] text-gray-600 italic">Manage representatives from the partner portal.</p>
        </div>
      );
    }
    // list types → chip/tag input (multi-value, no comma-merge bug)
    if (LIST_TYPES.has(f.type)) {
      return (
        <TagInput label={f.label} required={f.required} value={v}
          onChange={(next) => set(f.key, next)} />
      );
    }
    // plain scalars
    return (
      <div>
        <label className={labelCls}>{f.label}{req}</label>
        <input className={inputCls} value={v ?? ""} disabled={ro}
          onChange={(e) => set(f.key, e.target.value)} />
        {ro && READONLY_REASON[f.key] && <p className="text-[10px] text-gray-600 mt-1">{READONLY_REASON[f.key]}</p>}
      </div>
    );
  };

  if (loading) return <div className="text-gray-500 text-xs py-3">Loading questionnaire…</div>;
  if (!data) return <div className="text-red-400 text-xs py-3">{err || "Failed to load."}</div>;

  return (
    <div className="mt-3 border-t border-gray-800 pt-3">
      {err && <div className="mb-2 px-3 py-2 bg-red-900/40 border border-red-700 rounded-lg text-red-300 text-xs">{err}</div>}
      {msg && <div className="mb-2 px-3 py-2 bg-green-900/40 border border-green-700 rounded-lg text-green-300 text-xs">{msg}</div>}

      <div className="space-y-4">
        {data.steps.filter((s) => (s.fields || []).length > 0).map((s) => {
          const incomplete = data.status?.missing_steps?.includes(s.key);
          return (
            <div key={s.key}>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">{s.label}</span>
                {s.required && (
                  <span className={`text-[9px] px-1.5 py-0.5 rounded ${incomplete ? "bg-yellow-900/40 text-yellow-400" : "bg-green-900/40 text-green-400"}`}>
                    {incomplete ? "incomplete" : "done"}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {s.fields.map((f) => <div key={f.key}>{renderField(f)}</div>)}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex justify-end mt-4">
        <button onClick={() => void save()} disabled={saving}
          className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-xs font-medium disabled:opacity-50">
          {saving ? "Saving…" : "Save onboarding"}
        </button>
      </div>
    </div>
  );
}
